import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Download, FileText, Clock } from 'lucide-react';
import { toast } from 'sonner';

import { adminApi } from '../api/examApi';
import { DashboardLayout } from '../components/layout';
import PdfExamViewer from '../components/PdfExamViewer';

const STATUS_LABELS = {
  unpublished: { label: 'Unpublished', color: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300' },
  open: { label: 'Open', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
  closed: { label: 'Closed', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
};

function examStatus(exam) {
  if (exam.is_published !== 'published') return 'unpublished';
  return exam.submissions_open ? 'open' : 'closed';
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function AdminExamDetailPage() {
  const { examId } = useParams();
  const [exam, setExam] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfLoadError, setPdfLoadError] = useState(false);
  const blobUrlRef = useRef(null);

  useEffect(() => {
    return () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [examsRes, subsRes] = await Promise.all([
        adminApi.listExams(),
        adminApi.getExamSubmissions(examId),
      ]);
      const found = examsRes.data.find((e) => e.id === examId);
      setExam(found || null);
      setRows(subsRes.data);
    } catch {
      toast.error('Failed to load exam');
    } finally {
      setLoading(false);
    }
  }, [examId]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (!exam?.pdf_url) return;
    let cancelled = false;
    setPdfLoadError(false);
    adminApi.getQuestionPdf(examId)
      .then((res) => {
        if (cancelled) return;
        const blob = new Blob([res.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = url;
        setPdfBlobUrl(url);
      })
      .catch(() => {
        if (!cancelled) setPdfLoadError(true);
      });
    return () => { cancelled = true; };
  }, [examId, exam]);

  const handleDownload = async (apiFn, filename) => {
    try {
      const res = await apiFn();
      downloadBlob(res.data, filename);
    } catch {
      toast.error('Download failed');
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Exam Detail">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 rounded-xl bg-slate-100 dark:bg-slate-700" />
          ))}
        </div>
      </DashboardLayout>
    );
  }

  if (!exam) {
    return (
      <DashboardLayout title="Exam Detail">
        <p className="text-slate-500 dark:text-slate-400">Exam not found.</p>
      </DashboardLayout>
    );
  }

  const status = examStatus(exam);
  const statusCfg = STATUS_LABELS[status];

  return (
    <DashboardLayout title="Exam Detail">
      <div className="animate-fade-in space-y-6">
        <Link
          to="/admin/exams"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
        >
          <ArrowLeft size={16} /> Back to Exam Oversight
        </Link>

        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="text-2xl font-heading font-bold text-slate-900 dark:text-white">{exam.title}</h2>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                {exam.subject ?? '—'} &middot; {exam.faculty_name} ({exam.faculty_email})
              </p>
            </div>
            <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${statusCfg.color}`}>
              {statusCfg.label}
            </span>
          </div>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-slate-500 dark:text-slate-400">Duration</p>
              <p className="font-medium text-slate-900 dark:text-white">{exam.duration_minutes} min</p>
            </div>
            <div>
              <p className="text-slate-500 dark:text-slate-400">Total Marks</p>
              <p className="font-medium text-slate-900 dark:text-white">{exam.total_marks}</p>
            </div>
            <div>
              <p className="text-slate-500 dark:text-slate-400">Assigned</p>
              <p className="font-medium text-slate-900 dark:text-white">{exam.assignment_count}</p>
            </div>
            <div>
              <p className="text-slate-500 dark:text-slate-400">Submitted</p>
              <p className="font-medium text-slate-900 dark:text-white">{exam.submission_count}</p>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 shadow-sm">
          <h3 className="mb-3 font-medium text-slate-900 dark:text-white">Question Paper</h3>
          {exam.pdf_url ? (
            pdfBlobUrl ? (
              <PdfExamViewer src={pdfBlobUrl} title="Question Paper" className="min-h-[520px]" />
            ) : pdfLoadError ? (
              <p className="text-sm text-red-500">Failed to load question paper.</p>
            ) : (
              <p className="text-sm text-slate-500 dark:text-slate-400">Loading question paper...</p>
            )
          ) : (
            <p className="text-sm text-slate-500 dark:text-slate-400">No PDF uploaded for this exam yet.</p>
          )}
        </div>

        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden shadow-sm">
          <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700">
            <h3 className="font-medium text-slate-900 dark:text-white">Assignments &amp; Submissions</h3>
          </div>
          {rows.length === 0 ? (
            <div className="py-16 text-center text-slate-400 text-sm flex flex-col items-center gap-2">
              <FileText size={28} className="text-slate-300 dark:text-slate-600" />
              No students assigned to this exam yet.
            </div>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-700">
              {rows.map((row) => (
                <div key={row.assignment_id} className="px-6 py-4" data-testid={`assignment-row-${row.assignment_id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">{row.student_name}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {row.student_email} &middot; Class {row.student_class ?? '—'}
                      </p>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-500 dark:text-slate-400">
                      <span className="capitalize">{row.status.replace('_', ' ')}</span>
                      {row.marks !== null && (
                        <span className="font-semibold text-slate-900 dark:text-white">Marks: {row.marks}</span>
                      )}
                      {row.corrected_paper_url && (
                        <button
                          onClick={() => handleDownload(
                            () => adminApi.downloadReviewed(row.attempt_id),
                            `corrected_${row.student_name}.pdf`
                          )}
                          className="inline-flex items-center gap-1 text-primary dark:text-accent-cool hover:underline"
                        >
                          <Download size={14} /> Corrected Paper
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-500 dark:text-slate-400">
                    <span>Started: {row.started_at ? new Date(row.started_at).toLocaleString() : '—'}</span>
                    <span>&middot;</span>
                    <span>Submitted: {row.submitted_at ? new Date(row.submitted_at).toLocaleString() : '—'}</span>
                  </div>
                  {row.submissions.length > 0 && (
                    <div className="mt-3 space-y-1.5">
                      {row.submissions.map((s, idx) => (
                        <div
                          key={s.id}
                          className="flex items-center justify-between rounded-md bg-slate-50 dark:bg-slate-900/50 px-3 py-2 text-sm"
                          data-testid={`submission-row-${s.id}`}
                        >
                          <span className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                            <Clock size={13} />
                            Upload {idx + 1} &middot; {new Date(s.submitted_at).toLocaleString()}
                            {s.is_late && (
                              <span
                                className="inline-flex items-center rounded-full bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300 px-2 py-0.5 text-xs font-semibold"
                                data-testid={`late-badge-${s.id}`}
                              >
                                Late
                              </span>
                            )}
                          </span>
                          <button
                            onClick={() => handleDownload(
                              () => adminApi.downloadSubmission(s.id),
                              `submission_${row.student_name}_${idx + 1}.pdf`
                            )}
                            className="inline-flex items-center gap-1 text-primary dark:text-accent-cool hover:underline"
                          >
                            <Download size={14} /> View
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
